/**
 * Journey engine (spec section 6, wave 8): the shapes shared by the rules, the engine, the ledgers,
 * the API and the surfaces. Rules are data (utils/journey/rules); the engine is pure
 * (utils/journey/engine.ts); server/journey/ builds the context and talks to storage.
 */

// ---------------------------------------------------------------------------------------------
// J0: situation, stage history
// ---------------------------------------------------------------------------------------------

export const SITUATION_KINDS = ['in_work', 'in_work_looking', 'under_notice', 'out_of_work', 'student_graduate', 'new_to_finland'] as const;
export type SituationKind = (typeof SITUATION_KINDS)[number];

/** Set by the person, never inferred. `noticeEnd` (YYYY-MM-DD) only for `under_notice`. */
export interface Situation {
  kind: SituationKind;
  noticeEnd?: string;
  setAt: string;
}

export function isSituationKind(value: unknown): value is SituationKind {
  return typeof value === 'string' && (SITUATION_KINDS as readonly string[]).includes(value);
}

/** A tracked job's position on the board: the Saved column, or the applied item's stage. */
export const JOURNEY_STAGES = ['saved', 'applied', 'interviewing', 'offer', 'rejected', 'hired'] as const;
export type JourneyStage = (typeof JOURNEY_STAGES)[number];

export interface StageHistoryEntry {
  to: JourneyStage;
  /** ISO instant. */
  at: string;
}

// ---------------------------------------------------------------------------------------------
// J0: ledgers
// ---------------------------------------------------------------------------------------------

/** UserState item `nudge#{ruleId}` (server-owned, not a UserStateItemKey). */
export interface NudgeRecord {
  shownCount: number;
  lastShownAt: string | null;
  actedAt: string | null;
  dismissCount: number;
  /** ISO instants of the last dismissals (newest last, capped), for the 90-day penalty and the weekly pause. */
  dismissals: string[];
  snoozedUntil: string | null;
  retired: boolean;
  /** Per-subject (interview, application, offer, season) last shown, so per-subject rules fire once per subject. */
  subjects: Record<string, string>;
  /** Times each subject has been shown (subject to count), so a per-subject `maxShows` holds. Absent on older rows: one show. */
  counts?: Record<string, number>;
}

export const EMPTY_NUDGE_RECORD: NudgeRecord = {
  shownCount: 0,
  lastShownAt: null,
  actedAt: null,
  dismissCount: 0,
  dismissals: [],
  snoozedUntil: null,
  retired: false,
  subjects: {},
};

export type DeliveryChannel = 'email' | 'push' | 'web_push' | 'telegram' | 'in_app';

export interface DeliverySend {
  channel: DeliveryChannel;
  /** ISO instant. */
  at: string;
  /** Rule id of a journey send, or a free label ("scout", "monday_plan", "season") for other non-alert sends. */
  label: string;
  /** Trigger key (`ruleId|subject`): the same trigger is never sent on two channels. */
  trigger?: string;
}

/** Per-user record of non-alert sends across channels (Plus table, TTL-bound). Alerts are not counted. */
export interface DeliveryLedger {
  sends: DeliverySend[];
  /** Consecutive pushes sent that were not opened. */
  unopenedPushes: number;
  /** Introductions shown (any surface), newest last, for the weekly and per-visit caps. */
  introductions: Array<{ ruleId: string; at: string }>;
  /** Introductions are paused until this ISO instant (2 dismissals in a week). */
  introPausedUntil: string | null;
  /** Generic upgrade prompts shown, newest last. */
  upgrades: Array<{ ruleId: string; at: string; contextual: boolean }>;
  /** The nudge currently on show per in-app surface, so a refresh inside the same visit shows the same one and counts once. */
  surfaceShown?: Record<string, WireNudge & { at: string; pageKey?: string; withheld?: boolean }>;
}

export const EMPTY_DELIVERY_LEDGER: DeliveryLedger = {
  sends: [],
  unopenedPushes: 0,
  introductions: [],
  introPausedUntil: null,
  upgrades: [],
};

// ---------------------------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------------------------

export interface JourneyEvent {
  /** Analytics event name, for example `srp_view`. */
  name: string;
  /** Epoch ms. */
  at: number;
  params: Record<string, unknown>;
}

export interface JourneyInterview {
  id: string;
  /** Epoch ms of the start. */
  at: number;
  /** True when only the date is known (no clock time). */
  dateOnly: boolean;
  place: string | null;
  address: string | null;
  /** Straight-line or route distance from home in km, when both ends are known. */
  distanceKm: number | null;
  /** True when the distance is an estimate (straight line x 1.3), not a routed distance. */
  distanceEstimate?: boolean;
  /** Leave-by time text, when the leave-by service could compute one. */
  leaveBy: string | null;
  hasPack: boolean;
  packOpenedAt: number | null;
  debriefed: boolean;
  followUpSent: boolean;
  /** Epoch ms the travel claim was marked sent, if ever. */
  travelClaimSentAt: number | null;
  source: 'tracker' | 'mail';
}

export interface JourneyOffer {
  enteredAt: number | null;
  monthlyGross: number | null;
  municipality: string | null;
  /** Reply deadline entered by the person (epoch ms), else null. */
  replyDeadline: number | null;
  /** True when pay is below the median for the role and region and there is enough data. */
  payBelowMedian: boolean | null;
  fixedTerm: boolean;
  answered: boolean;
  negotiationOpened: boolean;
  comparisonOpened: boolean;
  detailsEnteredAt: number | null;
}

export interface JourneyTrackedJob {
  jobId: string;
  title: string | null;
  companyId: string | null;
  companyName: string | null;
  stage: JourneyStage;
  savedAt: number | null;
  appliedAt: number | null;
  /** Latest stage entry, else appliedAt, else savedAt. */
  stageChangedAt: number | null;
  stageHistory: StageHistoryEntry[];
  /** Whether the person added the job by hand (pasted link) rather than saving from the site. */
  addedByHand: boolean;
  deadline: number | null;
  /** The employer has answered (mail on the application address, or the stage moved past applied). */
  hasReply: boolean;
  employerReplyMedianDays: number | null;
  employerReplySamples: number;
  /** Any edit since the stage changed (archived or updated). */
  updatedAfterStage: boolean;
  /** Last time the person touched the application (any edit), epoch ms. */
  lastTouchedAt: number | null;
  followUpDraftUsedAt: number | null;
  interviews: JourneyInterview[];
  offer: JourneyOffer | null;
  /** Interview invite parsed from mail that the person has not saved yet. */
  unsavedMailInvites: Array<{ id: string; at: number; parsedAt: number }>;
  /** Fixed-term contract on the ad. */
  fixedTerm: boolean;
}

export interface JourneyCurrentJob {
  title: string | null;
  payFteEur: number | null;
  contractType: 'permanent' | 'fixed_term' | 'other' | null;
  /** YYYY-MM-DD. */
  contractEndDate: string | null;
  readyForMarket: boolean;
}

export interface JourneyContext {
  userId: string;
  /** The member's language for the copy (en when absent): the request locale in-app, the stored language for email and push. */
  locale?: 'en' | 'fi' | 'sv';
  /** Epoch ms. */
  now: number;
  /** Money tier. `grant` is a free-access member (hired-keep / free request). */
  membership: 'none' | 'plus' | 'pro' | 'grant';
  accountCreatedAt: number | null;
  visit: {
    /** Epoch ms the current visit started (30 minutes of idle ends a visit). */
    startedAt: number;
    pageViews: number;
    /** True from the account's first signed-in visit. */
    firstSignedInVisit: boolean;
    platform: 'web' | 'app';
    /** Epoch ms of the person's last activity on site or app (for "push only if not opened since"). */
    lastActiveAt: number | null;
  };
  situation: Situation | null;
  tracked: JourneyTrackedJob[];
  currentJob: JourneyCurrentJob | null;
  /** Latest hire (stage history to hired), epoch ms. */
  hiredAt: number | null;
  profile: {
    hasCvContent: boolean;
    /** Identifies the current CV content version (APP-6 fires once per version). */
    cvVersion: string | null;
    /** Years since the oldest role or degree ended; null when no dates. */
    oldestEndedYearsAgo: number | null;
    finnishLevel: 'none' | 'a1' | 'a2' | 'b1' | 'b2' | 'c1' | 'c2' | 'native' | null;
    homeMunicipality: string | null;
    /** Required applications per month for the activation model (out-of-work obligation), null = not configured. */
    applicationTarget: number | null;
    /** Applications this month that qualify for the obligation. */
    qualifyingThisMonth: number;
  };
  searches: {
    savedSearchCount: number;
    usesDefaultChannels: boolean;
    hasPushToken: boolean;
    hasTelegram: boolean;
    remoteInstantAlert: boolean;
    deadlineAlertCount: number;
  };
  features: {
    scoutBrief: boolean;
    applicationAddress: boolean;
    quietWatchOn: boolean;
    programmeWatchCount: number;
    extensionInstalled: boolean;
    shareSheetUsed: boolean;
    connections: number;
    appInstalled: boolean;
  };
  /** ProductEvents of the last 30 days, oldest first. */
  events: JourneyEvent[];
  /** Card shelf reminders due today (60 days or less to expiry, not yet reminded for that expiry date). Absent: none. */
  cardReminders?: Array<{ cardId: string; name: string; expiresOn: string; daysLeft: number }>;
  /** The page the request came from (request-time surfaces). */
  page?: {
    job?: {
      jobId: string;
      /** Days the ad has been open, null unknown. */
      daysOpen: number | null;
      repostChainCount: number;
      /** Epoch ms first seen. */
      firstSeen: number | null;
      finnishRequired: boolean;
      remote: boolean;
    };
    search?: { remote: boolean; summer: boolean };
    cvStudio?: boolean;
    myJobs?: boolean;
  };
  /** What just happened (event-time evaluation). */
  trigger?: {
    type: 'stage_change' | 'job_saved' | 'saved_search_created' | 'situation_set' | 'offer_entered';
    jobId?: string;
    from?: JourneyStage | null;
    to?: JourneyStage;
    manual?: boolean;
  };
  /** Rule-specific flags (feature flags for rules whose target feature is not built or not live). */
  flags: Record<string, boolean>;
  ledger: {
    nudges: Record<string, NudgeRecord>;
    delivery: DeliveryLedger;
  };
}

// ---------------------------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------------------------

export type RuleKind = 'introduce' | 'remind' | 'always' | 'stage_moment';
export type RuleIntent = 'reminder' | 'stage_moment' | 'situation' | 'discovery' | 'upgrade';
export type RuleStage = 'set' | 'src' | 'app' | 'int' | 'dec' | 'wrk' | 'btw' | 'any';

/** In-app surfaces (request time and event time) plus the outbound channels. */
export type JourneySurface =
  | 'welcome'
  | 'my_jobs'
  | 'job_page'
  | 'toast'
  | 'search_header'
  | 'cv_studio'
  | 'account'
  | 'offer_card'
  | 'fullscreen'
  | 'timeline'
  | 'scout'
  | 'email'
  | 'push';

export const IN_APP_SURFACES: readonly JourneySurface[] = [
  'welcome',
  'my_jobs',
  'job_page',
  'toast',
  'search_header',
  'cv_studio',
  'account',
  'offer_card',
  'fullscreen',
  'timeline',
  'scout',
];

export interface RuleHit {
  /** Stable subject (interview id, job id, season key, offer job id); the rule fires once per subject when `perSubject`. */
  subject?: string;
  /** The dated event the nudge is about (epoch ms): drives urgency and the tie-break. */
  eventAt?: number;
  /** Job the nudge is about, for the card's link. */
  jobId?: string;
  /** Values the copy needs. */
  vars?: Record<string, string | number>;
  /** Earliest instant the nudge may be delivered (for scheduled rules); null/undefined = now. */
  notBefore?: number;
  /** A reminder hit of an otherwise introducing rule (INT-7 day 25): scored and budgeted as a reminder. */
  asReminder?: boolean;
}

export interface NudgeCopy {
  title: string;
  body: string;
  cta: string;
  /** Path on jobcrawls.com (no locale prefix). */
  url: string;
}

export interface JourneyRule {
  id: string;
  /** Position in the spec table: lower = older, used as the second tie-break. */
  order: number;
  name: string;
  stage: RuleStage;
  kind: RuleKind;
  intent: RuleIntent;
  surfaces: JourneySurface[];
  /** Evaluate against the context. Returns a hit (possibly several subjects) or null. */
  evaluate(ctx: JourneyContext): RuleHit[];
  /** When true the ledger tracks shows per hit subject instead of per rule. */
  perSubject?: boolean;
  /** Max shows ever (per subject when perSubject). */
  maxShows?: number;
  /** Minimum days between shows. */
  cooldownDays?: number;
  /** Days after a dismissal before the rule can show again (default 30, the "Not now" snooze). */
  dismissSnoozeDays?: number;
  /** Feature-flag key in `ctx.flags` that must be true; when absent the rule is on. */
  flag?: string;
  /** The named success event and the window it counts in. null for rules with no success (SRC-4, INT-4). */
  success: { event: string; windowDays: number; param?: { key: string; pattern: string } } | null;
  /** Narrows a hit to some surfaces (for example push only 2 days before). Absent: every listed surface. */
  appliesTo?(hit: RuleHit, surface: JourneySurface, ctx: JourneyContext): boolean;
  /** Email only when the person has not opened the app or site since the dated event. */
  emailOnlyIfUnseen?: boolean;
  /** Interview-day item: quiet hours end at 07:00 instead of 08:00. */
  interviewDay?: boolean;
  /** Time-critical reminder: not held back by the weekly email/push caps (still by quiet hours, the daily push cap and the same-trigger rule). */
  urgent?: boolean;
  copy(hit: RuleHit, ctx: JourneyContext): NudgeCopy;
}

export interface ScoredNudge {
  rule: JourneyRule;
  hit: RuleHit;
  score: number;
  copy: NudgeCopy;
}

export interface WireNudge {
  ruleId: string;
  subject: string | null;
  surface: JourneySurface;
  score: number;
  title: string;
  body: string;
  cta: string;
  url: string;
  intent: RuleIntent;
}
